import { AlertColor, Button, CircularProgress, Grid, Link, Modal, TextField, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import React, { useImperativeHandle } from 'react';
import { globalTheme } from '../../../common/theme';
import { SnackBarMessage, SnackBarMessageProps } from '../../component/SnackBarMessage';
import { useAppSelector } from '../../../common/hooks';
import { RootState } from '../../../common/Store';
import Title from '../../component/Title';
import { GeneralDialog } from '../../component/GeneralDialog';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margin: {
            margin: theme.spacing(1),
        },
        modalPaper: {
            position: 'absolute',
            width: 400,
            backgroundColor: theme.palette.background.paper,
            border: '2px solid #000',
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
        },
        control: {
            marginBottom: theme.spacing(1)
        }
    }),

);

export interface PackingListViewerHandle {
    Initialise: (packingListNo: string, showDescription: boolean, showCargoReceivedBy: boolean) => void,
}

export interface PackingListViewerProps {
    onClose: Function;
}
  
export const PackingListViewer = React.forwardRef((props: PackingListViewerProps, ref: React.Ref<PackingListViewerHandle>) => {
    const style = { ...useStyles(), ...globalTheme() };
    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState<any>();
    const [packingListNo, setPackingListNo] = React.useState<string>();
    const [showDescription, setShowDescription] = React.useState<boolean>(true);
    const [showCargoReceivedBy, setShowCargoReceivedBy] = React.useState<boolean>(true);

    //Dialogs and snacks
    const hasChanges = React.useRef<boolean>(false);
    const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
    const [snackSettings, setSnackSettings] =
    React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
    });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
        setSnackSettings({
            open: true,
            message: message,
            subMessage: subMessage,
            severity: colour,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });
    }


    const [viewerUrl, setViewerUrl] = React.useState<string>("");

    useImperativeHandle(ref, () => ({

        Initialise(packingListNo: string, showDescription: boolean, showCargoReceivedBy: boolean) {
            setOpen(true);
            setLoading(true);
            hasChanges.current = false;

            setShowDescription(showDescription);
            setShowCargoReceivedBy(showCargoReceivedBy);
            setPackingListNo(packingListNo);
            GenerateReport(packingListNo, showDescription, showCargoReceivedBy);
        }

    }));

    function Export(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: string) {
        const params = { packingListNo: i, showDescription: showDescription, showCargoReceivedBy: showCargoReceivedBy };
        axios.get(`Api/V1/PackingList/Export`, {
            params,
            headers: {
                'Content-Encoding': 'gzip'
            },
            responseType: 'blob'
        })
            .then((result) => {
                const file = new Blob([result.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;" });
  
                const url = window.URL.createObjectURL(file);
  
                const link = document.createElement("a");
                document.body.appendChild(link);
                link.href = url;
                link.download = i + ".xls";
                link.click();
  
                setLoading(false);
            },
                (error) => {
                    console.log(error);
                    setLoading(false);
                    showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                }
            )
    }
  
    function closeModal() {
        setDialogOpen(false)
        setOpen(false);
    }

    function GenerateReport(i: string, showDescription: boolean, showCargoReceivedBy: boolean) {
        const params = { packingListNo: i, showDescription: showDescription, showCargoReceivedBy: showCargoReceivedBy };
        axios.get(`Api/V1/PackingList/Preview`, {
            params,
            headers: {
                'Content-Encoding': 'gzip'
            },
            responseType: 'blob'
        })
            .then((result) => {
                const file = new Blob([result.data], { type: "application/pdf" });

                const url = window.URL.createObjectURL(file);

                setViewerUrl(url);
                setLoading(false);
            },
                (error) => {
                    console.log(error);
                    setLoading(false);
                    showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                }
            )
    }

    return (
        <div>
            <Modal
                open={open}
             >
                <div className={style.ModalMain}>

                    {!loading ? (
                        <div style={{ height: '100%' }}>
                            <Title pageTitle={packingListNo ?? ""} />

                            <div className={style.ModalHeading}>
                                <Typography variant="h4">{packingListNo}</Typography>
                            </div>

                            <div className={style.ModalContent} style={{height: '75vh'}}>
                                <iframe src={viewerUrl} width="100%" height="100%" style={{overflow: 'auto'}}>
                                </iframe>
                            </div>

                            <div className={style.ModalFooter}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={style.margin}
                                    onClick={(e) => Export(e, packingListNo ?? "")}>Export</Button>
                                <Button
                                    variant="contained"
                                    className={style.margin}
                                    onClick={(e) => closeModal()}>Close</Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress color="primary" />
                        </div>
                    )}
                </div>
            </Modal>

            <SnackBarMessage {...snackSettings} />

            <GeneralDialog title={"Save Changes?"} message={"Changes have been made, did you want to save before closing?"} open={dialogOpen} options={
                [
                    { text: 'No', action: (event) => closeModal(), color: "primary", variant: "contained" }
                ]
            } />
        </div >
    );
});
